import {loadStripe, Stripe} from '@stripe/stripe-js';
import {
  STRIPE_PUBLISHABLE_KEY,
  STRIPE_PUBLISHABLE_KEY_TEST,
} from '../constants/apiKeys';
import {
  getBasePrice,
  getCurrentProduct,
  getListPrice,
  SkuDynamicResponse,
  formatPrice,
} from './dynamicStoreData';
import {StoreRegionData} from './storeGeoLocator';

let stripePromise: Promise<Stripe | null> | undefined;

export const getStripe = () => {
  const isLive = true;

  if (!stripePromise) {
    stripePromise = loadStripe(
      isLive ? STRIPE_PUBLISHABLE_KEY : STRIPE_PUBLISHABLE_KEY_TEST
    );
  }
  return stripePromise;
};

export const getDiscountTagText = (
  currentBasePrice: any,
  currentListPrice: any | undefined,
  currency: any,
  save: string
) => {
  if (!currentBasePrice || !currentListPrice) return null;

  const amount = currentBasePrice.unitAmount - currentListPrice.unit_amount;

  if (amount < 1) return null;

  const formattedAmount = formatPrice(currency, amount);

  return `${save} ${formattedAmount}`;
};

export type DynamicStoreState = {
  [key: string]: SkuDynamicResponse[];
};

export const getProductOptions = (
  sortedProducts: any,
  dynamicStoreData: DynamicStoreState | undefined,
  storeCountry: StoreRegionData[],
  stripePrices: any[],
  save: string
) => {
  if (!dynamicStoreData || !storeCountry) return undefined;

  return sortedProducts.map(({node}: any) => {
    const {skuIdentifier, basePrice, title, shortDescription} = node;
    const dynamicData = dynamicStoreData[skuIdentifier];

    const currentProduct = getCurrentProduct(dynamicData);

    if (!currentProduct) return undefined;

    const currentBasePrice = getBasePrice(basePrice, storeCountry);
    const currentListPrice = getListPrice(
      stripePrices,
      currentProduct,
      storeCountry
    );

    if (!currentListPrice || !currentBasePrice) return undefined;

    const discountTag = getDiscountTagText(
      currentBasePrice,
      currentListPrice,
      storeCountry[0].currency,
      save
    );

    const formattedAmount = formatPrice(
      storeCountry[0].currency,
      currentListPrice.unit_amount
    );

    const formattedBaseAmount = formatPrice(
      storeCountry[0].currency,
      currentBasePrice.unitAmount
    );

    return {
      title,
      description: shortDescription,
      priceSummary: formattedAmount,
      basePriceSummary: formattedBaseAmount,
      discount: discountTag,
      currency: currentBasePrice.currency,
      unitAmount: currentBasePrice.unitAmount,
      skuIdentifier,
    };
  });
};

export const getProductOptionsWithOptionalPrice = (
  sortedProducts: any,
  dynamicStoreData: DynamicStoreState | undefined,
  storeCountry: StoreRegionData[],
  stripePrices: any[],
  save: string
) => {
  if (dynamicStoreData) {
    return getProductOptions(
      sortedProducts,
      dynamicStoreData,
      storeCountry,
      stripePrices,
      save
    );
  }

  return sortedProducts.map(({node}: any) => {
    const {skuIdentifier, basePrice, title, shortDescription} = node;

    const currentBasePrice = getBasePrice(basePrice, storeCountry);

    const formattedBaseAmount = formatPrice(
      storeCountry[0].currency,
      currentBasePrice.unitAmount
    );

    return {
      title,
      description: shortDescription,
      basePriceSummary: formattedBaseAmount,
      currency: currentBasePrice.currency,
      unitAmount: currentBasePrice.unitAmount,
      skuIdentifier,
    };
  });
};

export const getProductImage = (
  products: any,
  fallbackImage: any,
  selectedProduct: string | undefined
) => {
  const product = products.find(
    ({node}: any) => node.skuIdentifier === selectedProduct
  );

  if (!product) return fallbackImage;

  return product.node.images[0];
};

/**
 * THIS IS HACKY. USE SKU ID AND PROPERLY MATCH
 */
export const getPriceForName = (name: string, options: any[]) => {
  const nameSubstring = name.substring(0, 3);

  if (nameSubstring === 'App') return 'Free';

  const result = options.find((option) => {
    // ALl options are named 'Orca [product]'
    const optionSubstring = option.title.substring(5, 8);

    return nameSubstring === optionSubstring;
  });

  return result?.basePriceSummary;
};
